import React, { Fragment, useEffect, useState } from "react";
import { useTable, usePagination, useFilters } from "react-table";
import { useHistory, useLocation } from "react-router-dom";
import { txtTable } from "../../utils/texts";
import PropTypes from "prop-types";
import { Pagination } from "react-bootstrap";
import Loader from "../Loader";
import qs from "query-string";
import { DefaultColumnFilter, filterTypes } from "./Filters";
import "../../_metronic/_assets/sass/global/components/general/_darsupTable.scss";
import { PAGE_SIZE } from "../../utils/constants";

const DarsUpTable = (props) => {
  let {
    data,
    columns,
    hasPagination = false,
    hasFilter = false,
    hasSearch = false,
    sortFields,
    fetchData,
    hasCounterColumn = false,
    tablePageSize = PAGE_SIZE,
    count,
  } = props;

  const [searchValue, setSearchValue] = useState("");
  const [sortValue, setSortValue] = useState("");

  const params = new URLSearchParams(useLocation().search);

  const searchQuery = params.get("search");
  const pageNumberQuery = Number(params.get("page"));
  const ordering = params.get("ordering");

  const history = useHistory();

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );
  const tablePageCount = Math.ceil(count / tablePageSize);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    rows,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      initialState: {
        pageIndex: pageNumberQuery ? pageNumberQuery - 1 : 0,
        hiddenColumns: ["id"],
      },
      manualPagination: true,
      autoResetPage: false,
      autoResetSortBy: false,
      pageCount: tablePageCount,
    },
    hasFilter && useFilters,
    hasPagination && usePagination
  );

  const handleSearch = (e) => {
    e.preventDefault();
    if (hasSearch) {
      hasPagination && gotoPage(0);
      const query = {
        ...(hasSearch && searchValue && { search: searchValue || "" }),
        ...(hasPagination && { page: pageIndex ? pageIndex + 1 : 1 }),
        ...(sortValue && { ordering: sortValue || "" }),
      };
      const searchString = qs.stringify(query);
      history.push({
        search: searchString,
      });
    }
  };

  const handleSort = (column) => {
    if (column.hasSort) {
      if (!sortValue) {
        setSortValue(column.id);
      } else if (sortValue === column.id) {
        setSortValue(`-${column.id}`);
      } else if (sortValue[0] === "-") {
        setSortValue("");
      }
    }
  };

  useEffect(() => {
    const query = {
      ...(hasSearch && searchQuery && { search: searchValue || "" }),
      ...(hasPagination && { page: pageIndex ? pageIndex + 1 : 1 }),
      ...(sortValue && { ordering: sortValue }),
    };
    const searchString = qs.stringify(query);
    history.push({
      search: searchString,
    });
  }, [sortValue]);

  useEffect(() => {
    hasPagination && setPageSize(tablePageSize);
    //If there is any data in query params, add them
    // We set the Page Index from query param in Initial stae
    searchQuery && setSearchValue(searchQuery);
    ordering && setSortValue(ordering);
  }, []);

  useEffect(() => {
    if (pageNumberQuery && hasPagination) {
      gotoPage(pageNumberQuery - 1);

      let params = {
        page: hasPagination ? pageNumberQuery : 1,
        search: hasSearch ? searchValue || searchQuery : "",
        ordering: sortFields?.length ? sortValue : "",
      };

      fetchData(params, tablePageSize);
    }
  }, [pageNumberQuery]);

  useEffect(() => {
    if (searchQuery && hasSearch) {
      let params = {
        page: hasPagination ? pageNumberQuery : 1,
        search: hasSearch ? searchValue || searchQuery : "",
        ordering: sortFields?.length ? sortValue : "",
      };

      fetchData(params, tablePageSize);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (sortFields?.length) {
      let params = {
        page: hasPagination && pageNumberQuery ? pageNumberQuery : 1,
        search: hasSearch ? searchValue || searchQuery : "",
        ordering: sortFields?.length ? sortValue : "",
      };
      fetchData(params, tablePageSize);
    }
  }, [ordering]);

  // Page index changes whenever pagination has occurred
  useEffect(() => {
    if (hasPagination) {
      const query = {
        ...(hasSearch && searchQuery && { search: searchQuery || "" }),
        page: pageIndex ? pageIndex + 1 : 1,
        ...(sortValue && { ordering: sortValue || "" }),
      };
      const searchString = qs.stringify(query);
      history.push({
        search: searchString,
      });
    }
  }, [pageIndex]);

  return (
    <Fragment>
      {hasSearch && (
        <>
          <div className="mb-2 table-search">
            <form onSubmit={handleSearch}>
              <input
                value={searchValue || ""}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
                placeholder={`جستجو...`}
              />
              <i className="fas fa-search" onClick={handleSearch} />
            </form>
          </div>
        </>
      )}
      <table
        {...getTableProps()}
        className="table table-light table-striped table-bordered table-hover"
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {hasCounterColumn && (
                <th className="font-weight-bold" style={{ width: "5%" }}>
                  {txtTable.counter}
                </th>
              )}
              {headerGroup.headers.map((column) => {
                return (
                  <th className="font-weight-bold" {...column.getHeaderProps()}>
                    <span
                      className="d-flex align-items-center"
                      onClick={() => handleSort(column)}
                      style={
                        column.hasSort
                          ? { cursor: "pointer" }
                          : { cursor: "default" }
                      }
                    >
                      <span>{column.render("Header")}</span>
                      {column.hasSort && (
                        <>
                          {sortValue ? (
                            sortValue[0] === "-" ? (
                              <i
                                style={{ fontSize: "20px" }}
                                className="fas fa-sort-down mr-2"
                              />
                            ) : (
                              <i
                                style={{ fontSize: "20px" }}
                                className="fas fa-sort-up mr-2"
                              />
                            )
                          ) : (
                            ""
                          )}
                        </>
                      )}
                    </span>
                    <div>
                      {column.canFilter ? column.render("Filter") : null}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {hasPagination
            ? page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {hasCounterColumn && (
                      <td>
                        {Number(row.id) +
                          tablePageSize * (pageNumberQuery - 1) +
                          1}
                      </td>
                    )}
                    {row.cells.map((cell) => {
                      return (
                        <Fragment>
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        </Fragment>
                      );
                    })}
                  </tr>
                );
              })
            : rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {hasCounterColumn && <td>{Number(row.id) + 1}</td>}
                    {row.cells.map((cell) => {
                      return (
                        <Fragment>
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                            {!cell.value && "ندارد"}
                          </td>
                        </Fragment>
                      );
                    })}
                  </tr>
                );
              })}
        </tbody>
      </table>
      {hasPagination && (
        <Pagination className="d-flex justify-content-center custom-pagination">
          {tablePageCount > 1 && (
            <Pagination.Item
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              <i className="fas fa-chevron-right" />
            </Pagination.Item>
          )}
          {tablePageCount > 3 && pageIndex !== 0 && (
            <>
              <Pagination.Item
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                {1}
              </Pagination.Item>
              {pageIndex > 1 && <Pagination.Ellipsis disabled={true} />}
            </>
          )}

          <Pagination.Item active>{pageIndex + 1}</Pagination.Item>
          {tablePageCount > 3 && tablePageCount !== pageIndex + 1 && (
            <>
              {tablePageCount - (pageIndex + 1) !== 1 && (
                <Pagination.Ellipsis disabled={true} />
              )}
              <Pagination.Item
                onClick={() => gotoPage(tablePageCount - 1)}
                disabled={!canNextPage}
              >
                {tablePageCount}
              </Pagination.Item>
            </>
          )}
          {tablePageCount > 1 && (
            <Pagination.Item onClick={() => nextPage()} disabled={!canNextPage}>
              <i className="fas fa-chevron-left" />
            </Pagination.Item>
          )}
        </Pagination>
      )}
    </Fragment>
  );
};
DarsUpTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  hasPagination: PropTypes.bool,
  hasFilter: PropTypes.bool,
  hasCounterColumn: PropTypes.bool,
  hasSearch: PropTypes.bool,
  sortFields: PropTypes.array,
  fetchData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
};

export default DarsUpTable;
